import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { doc, setDoc, arrayUnion } from "firebase/firestore";
import { db } from "../Configuration/firebase";

// Push notifications for events. When a host changes an event's address, the
// onEventAddressChange Cloud Function pushes to everyone who joined. For that to
// reach a guest, this device's FCM token has to be on their user doc, so we
// register here and store the token in users/{uid}.fcmTokens[].
//
// We use @capacitor-firebase/messaging (same family as our auth plugin) rather
// than @capacitor/push-notifications because it returns a real FCM registration
// token on BOTH Android and iOS - the plain push plugin hands back the raw APNs
// token on iOS, which FCM's send-to-token API rejects. One code path, both
// platforms, and the Cloud Function stays as-is.
//
// Native-only: web push would need a service worker + VAPID key we haven't set
// up, so this is a deliberate no-op in the browser.

// The listener is process-wide and added once; currentUid is updated on each
// call so a token that arrives later is always saved under the current user
// (e.g. after a logout/login as someone else on the same device).
let listenerAdded = false;
let currentUid = null;

async function saveToken(token) {
  if (!currentUid || !token) return;
  try {
    await setDoc(
      doc(db, "users", currentUid),
      { fcmTokens: arrayUnion(token) },
      { merge: true },
    );
  } catch (err) {
    console.error("Failed to save push token:", err);
  }
}

// Requests notification permission (if not already decided), fetches the FCM
// token, and stores it. Safe to call repeatedly - it never re-prompts once the
// user has answered, and re-saving the same token is a harmless arrayUnion.
export async function registerPush(uid) {
  if (!Capacitor.isNativePlatform() || !uid) return;
  currentUid = uid;

  if (!listenerAdded) {
    listenerAdded = true;
    // Fires when FCM rotates the token; keep the stored one current.
    FirebaseMessaging.addListener("tokenReceived", (event) =>
      saveToken(event.token),
    );
  }

  try {
    let perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await FirebaseMessaging.requestPermissions();
    }
    if (perm.receive !== "granted") return;
    const { token } = await FirebaseMessaging.getToken();
    await saveToken(token);
  } catch (err) {
    console.error("Push registration failed:", err);
  }
}
